import "./onboarding-flow";

import { Storybook } from "@tento-ui/ui/storybook";
import { createBuiltinRegistry } from "../kinds/builtin";
import { FlowModel } from "../src/flow-model";
import type { Surface } from "../src/flow-model";
import { OnboardingFlow } from "./onboarding-flow";

// One flow mixing generic built-ins with a product-owned kind, shown on both
// surfaces. The same JSON yields a three-step carousel on desktop and a single
// standalone bridge card on mobile; nothing but the context differs.
const MIXED = FlowModel.parse({
  schema: "tento.onboarding.flow/v1",
  name: "storybook",
  steps: [
    {
      id: "phone",
      kind: "mobile-bridge",
      gate: { surface: "mobile" },
      props: {
        title: "You're in.",
        message: "This app is desktop-first today. Open it on your computer.",
        urlLine: "or open example.app on your computer",
      },
    },
    {
      id: "welcome",
      kind: "notice",
      props: {
        assetSrc: "illustration/stars",
        kicker: "Welcome",
        title: "Three quick steps",
        message: "Each step is a node in a JSON flow; this one is the built-in notice kind.",
      },
    },
    { id: "agree", kind: "demo.agree" },
    {
      id: "done",
      kind: "notice",
      props: {
        title: "All set",
        message: "Finish calls the product's onFinish.",
      },
    },
  ],
});

// A product-owned kind: Continue stays disabled until the box is ticked.
function registry() {
  return createBuiltinRegistry({
    sendDesktopLink: () =>
      new Promise((resolve) => setTimeout(() => resolve("kiana@example.com"), 800)),
  }).register(
    "demo.agree",
    ({ flow }) => {
      const $e = document.createElement("label");
      $e.style.cssText = "display:flex;gap:8px;align-items:center;justify-content:center;";
      const $box = document.createElement("input");
      $box.type = "checkbox";
      $box.addEventListener("change", () => flow.refresh());
      $e.append($box, "I read the product-owned step");
      return { element: $e, canProceed: () => $box.checked };
    },
    { surfaces: ["desktop"] },
  );
}

function mount(surface: Surface, model: FlowModel = MIXED): HTMLElement {
  const $flow = OnboardingFlow.make() as OnboardingFlow;
  $flow.style.height = surface === "mobile" ? "560px" : "640px";
  $flow.style.setProperty("--onboarding-flow-pad-y", "24px");
  void $flow.start({
    model,
    registry: registry(),
    app: null,
    context: { surface },
    syncHash: false,
    requested: null,
    onFinish: async () => {
      await new Promise((r) => setTimeout(r, 600));
      return false; // storybook: stay on the page, re-enable Finish
    },
  });
  return $flow;
}

export class OnboardingFlowStoryBook extends Storybook.Default {
  title(): string {
    return "Onboarding Flow";
  }

  examples(): Storybook.Example[] {
    return [
      { title: "Desktop: notice → product step (gated Continue) → Finish", render: () => mount("desktop") },
      { title: "Mobile: same JSON, only the gated bridge step", render: () => mount("mobile") },
      {
        title: "Standalone webview escape",
        render: () =>
          mount(
            "mobile",
            FlowModel.parse({
              schema: "tento.onboarding.flow/v1",
              name: "webview",
              steps: [{
                id: "escape",
                kind: "webview-escape",
                props: { platform: "ios", url: "https://example.app/onboarding" },
              }],
            }),
          ),
      },
      {
        title: "Empty flow (no active steps)",
        render: () =>
          mount("desktop", FlowModel.parse({ schema: "tento.onboarding.flow/v1", name: "empty", steps: [] })),
      },
    ];
  }
}
